import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import { dataService } from "../../../../Services/data/data.service";
import DatePicker from "../../../Shared/DatePicker";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../Shared/Select";
import Button from "../../../Shared/Buttons/Button";

export default function BulkDeleteMeasurementsDialog({ open, onClose, allMeasurements, onDeleteSuccess }) {
  const classes = useStyles();

  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Derive unique companies from already-loaded measurements
  const companies = useMemo(() => {
    const map = new Map();
    allMeasurements.forEach((m) => {
      if (m.companyId && !map.has(m.companyId)) {
        map.set(m.companyId, m.companyName);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allMeasurements]);

  // Derive unique project + registration point combinations for the selected company.
  // Projects can share a name, so the registration point disambiguates which one is targeted.
  const projectOptions = useMemo(() => {
    if (!selectedCompanyId) return [];
    const map = new Map();
    allMeasurements.forEach((m) => {
      if (m.companyId === Number(selectedCompanyId) && m.projectId) {
        const resourceName = m.resourceName || "";
        const key = `${m.projectId}|${resourceName}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            label: resourceName ? `${m.projectName} - ${resourceName}` : m.projectName,
          });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [allMeasurements, selectedCompanyId]);

  // Compute matching measurement IDs based on active filters
  const filteredIds = useMemo(() => {
    let result = [...allMeasurements];

    if (selectedCompanyId) {
      result = result.filter((m) => m.companyId === selectedCompanyId);
    }

    if (selectedProjectKeys.length > 0) {
      result = result.filter((m) => selectedProjectKeys.includes(`${m.projectId}|${m.resourceName || ""}`));
    }

    if (fromDate) {
      const from = new Date(fromDate);
      result = result.filter((m) => {
        const date = m.startTime ? new Date(m.startTime) : m.insertedOn ? new Date(m.insertedOn) : null;
        return date && date >= from;
      });
    }

    if (toDate) {
      const to = new Date(toDate);
      // include the full end day
      to.setHours(23, 59, 59, 999);
      result = result.filter((m) => {
        const date = m.startTime ? new Date(m.startTime) : m.insertedOn ? new Date(m.insertedOn) : null;
        return date && date <= to;
      });
    }

    return result.map((m) => m.id);
  }, [allMeasurements, selectedCompanyId, selectedProjectKeys, fromDate, toDate]);

  const hasFilters = selectedCompanyId || selectedProjectKeys.length > 0 || fromDate || toDate;
  const canDelete = hasFilters && filteredIds.length > 0 && !isDeleting;

  const handleCompanyChange = (val) => {
    setSelectedCompanyId(val);
    setSelectedProjectKeys([]);
  };

  const handleDelete = () => {
    setIsDeleting(true);
    dataService
      .setMeasurementsInactiveByIdList(filteredIds)
      .then(() => {
        toast.success(`${filteredIds.length} measurement${filteredIds.length !== 1 ? "s" : ""} removed`, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        onDeleteSuccess(filteredIds);
        handleClose();
      })
      .catch(() => {
        toast.error("A problem occurred while deleting the data", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleClose = () => {
    setSelectedCompanyId("");
    setSelectedProjectKeys([]);
    setFromDate("");
    setToDate("");
    onClose();
  };

  return (
    <Dialog
      className={classes.dialog}
      open={open}
      onClose={handleClose}
      aria-labelledby="bulk-delete-dialog-title"
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="bulk-delete-dialog-title">
          Delete by filter
        </DialogTitle>
        <button onClick={handleClose} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>

      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>

          {/* Company */}
          <div className={classes.fieldGroup}>
            <label className={classes.labelText}>Company</label>
            <Select
              value={selectedCompanyId}
              onChange={(val) => handleCompanyChange(val)}
              placeholder="All companies"
              searchable
              fullwidth
            >
              <SelectTrigger>
                {selectedCompanyId
                  ? companies.find((c) => c.id === selectedCompanyId)?.name
                  : undefined}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All companies</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id} searchText={c.name}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Projects — multi-select, shown as "project - registration point" */}
          {selectedCompanyId && projectOptions.length > 0 && (
            <div className={classes.fieldGroup}>
              <label className={classes.labelText}>
                Projects
              </label>
              <Select
                value={selectedProjectKeys}
                onChange={setSelectedProjectKeys}
                placeholder="All projects"
                multiSelect
                searchable
                fullwidth
              >
                <SelectTrigger>
                  {selectedProjectKeys.length === 0
                    ? undefined
                    : selectedProjectKeys.length === 1
                    ? projectOptions.find((p) => p.key === selectedProjectKeys[0])?.label
                    : `${selectedProjectKeys.length} projects selected`}
                </SelectTrigger>
                <SelectContent>
                  {projectOptions.map((p) => (
                    <SelectItem key={p.key} value={p.key} searchText={p.label}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date range */}
          <div className={classes.fieldGroup}>
            <label className={classes.labelText}>Date range</label>
            <div className={classes.dateRow}>
              <div className={classes.dateField}>
                <span className={classes.dateLabel}>From</span>
                <DatePicker
                  precision="day"
                  value={fromDate}
                  onChange={(val) => setFromDate(val)}
                />
              </div>
              <div className={classes.dateField}>
                <span className={classes.dateLabel}>To</span>
                <DatePicker
                  precision="day"
                  value={toDate}
                  onChange={(val) => setToDate(val)}
                />
              </div>
            </div>
          </div>

          {/* Match count */}
          {hasFilters && (
            <p className={filteredIds.length > 0 ? classes.matchCount : classes.matchCountEmpty}>
              {filteredIds.length} measurement{filteredIds.length !== 1 ? "s" : ""} match{filteredIds.length === 1 ? "es" : ""} this filter
            </p>
          )}
        </div>
      </DialogContent>

      <DialogActions
        sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}
      >
        <Button variant="outlined" onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button variant="destructive" startIcon={<Trash2 />} onClick={handleDelete} disabled={!canDelete}>
          {filteredIds.length > 0 && hasFilters
            ? `Delete ${filteredIds.length} measurement${filteredIds.length !== 1 ? "s" : ""}`
            : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "440px !important",
    maxWidth: "95vw !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",
    lineHeight: "100% !important",
    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
    padding: "3px !important",
    height: "25px !important",
    borderRadius: "3px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },
    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "8px",
    "&::-webkit-scrollbar": { display: "none" },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "stretch",
    gap: "12px",
    margin: "0px !important",
  },

  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  labelText: {
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",
    opacity: 0.8,
  },

  selectedCount: {
    fontWeight: "normal",
    textTransform: "none",
    color: "var(--color-primary)",
  },

  select: {}, // kept for safety — native select no longer used

  projectList: {}, // chips replaced by Select multi

  projectChip: {},

  projectChip__active: {},

  dateRow: {
    display: "flex",
    gap: "12px",
  },

  dateField: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  dateLabel: {
    fontSize: "12px",
    fontFamily: "Lato, sans-serif",
    color: "#666",
  },

  matchCount: {
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "var(--color-primary)",
    margin: "0",
    fontWeight: "600",
  },

  matchCountEmpty: {
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#999",
    margin: "0",
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",
    display: "block",
  },
}));
